import React, { useEffect, useState } from "react";
import { Button, Tabs, Tooltip } from "antd";
import { BsPencilSquare } from "react-icons/bs";
import {
    ArrowLeftOutlined,
    WarningOutlined, ArrowRightOutlined
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { setEditTarget } from "../../../store/components/BuildTargets.slice";
import { setConfigSelectedBehavior, setConfigSelectedTarget, setStep, setToEditData } from "../../../store/components/MarketExplorer.slice";
import TargetSelection from "../MarketExplorer/TargetSelection";
import BehaviorSelectionTree from "../MarketExplorer/BehaviorSelectionTree";
import BehaviorSelection from "../MarketExplorer/BehaviorSelection";
import { getMarketPackageCode, getMarketPackageName, getModuleName, saveTargetBuilder } from "../../../utils/oneintelligence/OneIntelligenceHelper";
import TargetInput from "../MarketExplorer/TargetInput";
import TargetView from "../MarketExplorer/TargetView";
import useShowInsights from "../../../hooks/useShowInsights";
import useHasReportData from "../../../hooks/useHasReportData";
import { setCompareInputTab } from "../../../store/components/Insights.slice";
import CrownSubscription from "../../ReportBuilder/Insights/CrownSubscription"
import ReportBuilderComparePage from "./ReportBuilderComparePage";
import NoSubCompareInput from "../NoSubscription/NoSubCompareInput";
import ScraperProgress from "../ScrapedKeywords/ScraperProgress/ScraperProgress";
import { setCollapseLSide, setExpandOverflow } from "../../../store/components/Expand.slice";
import Container from "../../General/Container/Container";

const { TabPane } = Tabs
function ReportBuilderLeftSide() {
    const dispatch = useDispatch();
    const [targetFocusData, setTargetFocusData] = useState([]);
    const { showInsights, hasInsightPermission } = useShowInsights()
    const hasData = useHasReportData();
    const marketPackageCode = getMarketPackageCode();
    const {
        compareInputTab
    } = useSelector((state) => state.insights);
    const {
        setup: {
            marketPackage,
            country
        },
        countryNotAvailable,
        scrapeTopicBrand
    } = useSelector((state) => state.buildTargets);
    const {
        configuration: {
            selectedTarget, selectedBehavior, toEditData, buildedTargets, savantReadMessage
        }, step
    } = useSelector((state) => state.marketExplorer);
    const { collapseLSide, expandOverflow } = useSelector((state) => state.expandstore)

    const renderHeader = () => {
        if (((step === 1 || step === 3) && !toEditData.mode) || (step === 2 && selectedTarget.code === "target_focus")) {
            return (
                <div className="targetBuilderHeader">
                    <div className="targetBuilderHeader--row">
                        <div className="marketExplorer--text">Market Explorer</div>
                        <div className="marketExplorer--edit">
                            <Button
                                type="link"
                                className="d-flex align-items-center gap-1"
                                style={{ padding: 0 }}
                                onClick={() => {
                                    dispatch(setEditTarget(true));
                                }}
                            >
                                <BsPencilSquare />
                                Edit
                            </Button>
                        </div>
                    </div>
                    <div className="targetBuilderHeader--row">
                        <div className="text">Product Package</div>
                        <div className="value">{getMarketPackageName()}</div>
                    </div>
                    <div className="targetBuilderHeader--row">
                        <div className="text">Module</div>
                        <div className="value">{getModuleName()}</div>
                    </div>
                    <div className="targetBuilderHeader--row">
                        <div className="text">Country</div>
                        {countryNotAvailable ? (
                            <div className="d-flex align-items-center gap-1">
                                <Tooltip placement="topLeft" title={`${country?.name} country in currently not available in this version.`}>
                                    <WarningOutlined style={{ color: "red" }} />
                                </Tooltip>
                                <div className="value" style={{ color: "var(--coolgray-400)" }}>{country?.name || ""}</div>
                            </div>
                        ) : <div className="value">{country?.name || ""}</div>}
                    </div>
                </div>
            );
        }
        return (
            <Button
                className="audience_details_header_left"
                type="link"
                disabled={savantReadMessage}
                icon={<ArrowLeftOutlined />}
                onClick={() => {
                    if (["behavior"].includes((selectedTarget?.code)) && selectedBehavior) {
                        dispatch(setConfigSelectedBehavior(null));
                    } else {
                        dispatch(setConfigSelectedTarget(null));
                        dispatch(setConfigSelectedBehavior(null));
                        dispatch(setToEditData({
                            mode: "", data: null
                        }));

                        if (toEditData.mode) {
                            dispatch(setStep(3));
                        } else {
                            dispatch(setStep(1));
                        }
                    }
                }}
                style={{ paddingLeft: "0px", marginBottom: "16px" }}
            >
                Back
            </Button>
        );
    };

    const checkIfTargetAlreadyInGroup = (code) => {
        if (!toEditData?.data) return false;
        const { audienceId } = toEditData.data;
        const find = buildedTargets.find((audience) => audience.key === audienceId);
        if (find) {
            return !!find.groups.find((group) => group.code === code);
        }
        return false;
    };
    const getBuilderContent = () => {
        if (step === 1) {
            return <TargetSelection />;
        }
        if (step === 2) {
            if (["behavior"].includes((selectedTarget?.code)) && !selectedBehavior && checkIfTargetAlreadyInGroup("target_focus")) {
                return <BehaviorSelectionTree />;
            }
            if (["behavior"].includes((selectedTarget?.code)) && !selectedBehavior) {
                return <BehaviorSelection />;
            }
            if (["target_focus"].includes((selectedTarget?.code)) && !selectedBehavior) {
                return (
                    <>
                        <BehaviorSelection targetFocusData={setTargetFocusData} />
                        <div
                            className="d-flex justify-content-end"
                            style={{
                                padding: "2mm 1mm"
                            }}
                        >
                            <Button
                                type="primary"
                                disabled={!targetFocusData.length}
                                onClick={() => {
                                    saveTargetBuilder(targetFocusData);
                                }}
                            >
                                Save
                            </Button>
                        </div>
                    </>
                );
            }
            if ((["behavior"].includes((selectedTarget?.code)) && selectedBehavior)
                || (["standard",
                    "behaviorConsumer",
                    "fbsensors",
                    "mokb_marketexplorer",
                    "mokb_marketexplorer_main",
                    "verticalpackages",
                    "seasonalpackages",
                    "ccsaudiences"].includes((selectedTarget?.code)))) {
                return <BehaviorSelectionTree />;
            }
            if (["wp_brands",
                "wp_generics",
                "wp_hcpsmedications~wp_hcps", "wp_hcpsmedications~wp_medications", "wp_hcps", "wp_medications"].includes(selectedTarget?.code)) {
                return <BehaviorSelectionTree />;
            }
            return <TargetInput />;
        }
        if (!showInsights) return <TargetView />

        return (
            <Tabs
                className="reportBuilder_targets_tabs"
                activeKey={compareInputTab}
                onChange={(e) => {
                    dispatch(setCompareInputTab(e));
                }}
            >
                <TabPane
                    tab="Audience"
                    key="audience"
                >
                    <TargetView />
                </TabPane>
                <TabPane
                    tab={(
                        <div className="d-flex gap-5p align-items-center">
                            <span>Compare</span>
                            <CrownSubscription />
                        </div>
                    )}
                    key="compare"
                    disabled={!hasData}
                >
                    {hasInsightPermission ? <ReportBuilderComparePage /> : <NoSubCompareInput />}
                </TabPane>
            </Tabs>
        );
    };
    const notWhitespace = marketPackageCode !== "whitespaces"
    return (
        <div
            className="reportBuilder-leftside_targets"
            style={{
                position: expandOverflow ? "absolute" : "relative",
                width: expandOverflow ? "500px" : "100%",
            }}
        >
            <Container className={`reportBuilder-leftside_expandbtn expand-button ${(expandOverflow || collapseLSide || notWhitespace) ? "singlearrow" : ""}`}>
                {(expandOverflow || notWhitespace) ? null : <Button
                    className="collapse"
                    icon={collapseLSide ? <ArrowRightOutlined /> : <ArrowLeftOutlined />}
                    type="link"
                    onClick={() => {
                        dispatch(setCollapseLSide(!collapseLSide));
                    }}
                />}

                {collapseLSide ? null : <Button
                    className=""
                    icon={expandOverflow ? <ArrowLeftOutlined /> : <ArrowRightOutlined />}
                    type="link"
                    onClick={() => {
                        dispatch(setExpandOverflow(!expandOverflow));
                    }}
                />}
            </Container>

            {renderHeader()}
            {getBuilderContent()}
            {!!scrapeTopicBrand.length ? <ScraperProgress /> : null}
        </div>
    );
}
export default ReportBuilderLeftSide;
